import { brandPathOf, mostSpecificBrandPathOf } from '@atlas/core-utils/classification'
import { workTypeOf } from '@atlas/core-utils/work-order'
import { AxisCode, FileType, MessageStatus, RequiredWorkOrderField, WorkType } from '@atlas/types'
import { TaskDetailsCard, type TaskDetailsField } from '@atlas/ui-kit'
import { useMemo, useState } from 'react'
import { SafeHtml } from '@/components/ui/SafeHtml'
import { ExternalDescription } from '@/components/work-orders/ExternalDescription'
import { isFromWorkfront } from '@/components/work-orders/WorkfrontBadges'
import type { WorkOrder } from '@/domain/work-order.types'
import { useBootstrapValues } from '@/hooks/useClassification'
import { useProductMessages } from '@/hooks/useExplore'
import { useWorkOrder } from '@/hooks/useWorkOrders'
import { narrowToApplicable } from '@/lib/axis-applicability'
import { notification } from '@/notifications/notification-store'
import { WorkOrderRefreshFromWorkfront } from '../../WorkOrders/WorkOrderWorkfrontHeader'
import type { StageMarket } from '../StageHeader'
import { sourceMaterialCodes } from '../stage-helpers'
import { CampaignCard } from './CampaignCard'
import { CampaignPicker } from './CampaignPicker'
import { CopyCard } from './CopyCard'
import { DeliverableDetailsCard } from './DeliverableDetailsCard'
import { LegalContentCard, type LegalContentRow } from './LegalContentCard'
import { DOCUMENT_FILES, INSTRUCTION_FILES, PackFileUploads } from './PackFileUploads'
import { isLegalContentOn, isRowApplicable, LEGAL_ROWS, type PackForm, type PackFormEdits } from './pack-form'
import { ReferenceMaterial } from './ReferenceMaterial'
import type { useCompleteDetails } from './useCompleteDetails'

const WORK_TYPE_LABEL: Readonly<Record<WorkType, string>> = {
	[WorkType.PRINT]: 'Print',
	[WorkType.DIGITAL]: 'Digital',
	[WorkType.VIDEO]: 'Video',
}

const copyToClipboard = (value: string) =>
	void navigator.clipboard.writeText(value).then(
		() => notification.success(`${value} copied.`),
		() => notification.error(`Could not copy ${value}.`),
	)

/** The delivery date as the Figma writes it: day/month/year. */
const deliveryDate = (iso: string): string =>
	new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })

/** An axis's values as dropdown items. */
function useItems(axis: AxisCode) {
	const values = useBootstrapValues(axis)
	return useMemo(() => values.map((v) => ({ id: v.code, label: v.label })), [values])
}

/** The request's read-only fields, in the Figma's order, leaving out what the order lacks. */
function useTaskDetails(wo: WorkOrder, brand: string | null, product: string | null, market: StageMarket | null) {
	const { data: parent } = useWorkOrder(wo.parentWorkOrderId)
	const workType = workTypeOf(wo.assetTypeCode)
	const rows: [string, string | null | undefined][] = [
		['Brand', brand],
		['Market', market?.name],
		['Parent Factory', parent?.orderCode],
		['Classification', wo.assetTypeCode],
		['Complexity', wo.complexityLevel],
		['Work type', workType ? WORK_TYPE_LABEL[workType] : null],
		['Work Order ID', wo.orderCode],
		['Work Order / request name', wo.title],
		['Parent Factory Brief', parent ? `${parent.orderCode}-${parent.title}` : null],
		['Product name', product],
		['Owner Market Country', market?.name],
		['Delivery Date', wo.dueAt ? deliveryDate(wo.dueAt) : null],
	]
	return rows
		.filter((row): row is [string, string] => !!row[1])
		.map(
			([label, value]): TaskDetailsField => ({
				id: label,
				label,
				value,
				flag: label === 'Market' && market?.flag ? <span aria-hidden="true">{market.flag}</span> : undefined,
			}),
		)
}

/**
 * The legal texts the order's brand or product holds for its market, by message type: read
 * brand-wide, then narrowed to the order's product path and market ladder.
 */
function useLegalTexts(wo: WorkOrder): Map<string, string> {
	const brands = useBootstrapValues(AxisCode.BRAND)
	const geographies = useBootstrapValues(AxisCode.GEOGRAPHY)
	const brandPath = mostSpecificBrandPathOf(brands, wo.tuple?.[AxisCode.BRAND])
	const { data: messages = [] } = useProductMessages(brandPath.at(-1) ?? null)
	const narrowing = {
		[AxisCode.BRAND]: brandPath,
		[AxisCode.GEOGRAPHY]: brandPathOf(geographies, wo.tuple?.[AxisCode.GEOGRAPHY]?.[0]),
	}
	const texts = new Map<string, string>()
	for (const message of narrowToApplicable(messages, narrowing)) {
		if (message.status === MessageStatus.ACTIVE && !texts.has(message.messageType)) {
			texts.set(message.messageType, message.content)
		}
	}
	return texts
}

function legalRowsOf(form: PackForm, texts: Map<string, string>): LegalContentRow[] {
	return LEGAL_ROWS.map(({ flag, label, messageType }) => {
		const text = messageType
			? (texts.get(messageType) ?? null)
			: flag === 'mainCopyApplies'
				? form.mainCopy || null
				: null
		return {
			flag,
			label,
			text,
			emptyText: messageType || flag === 'mainCopyApplies' ? 'No text' : 'No source yet',
			isApplicable: isRowApplicable(form, flag, text),
		}
	})
}

/** What every section reads: the order, the step's state, and whether the reader may edit. */
interface SectionProps {
	workOrder: WorkOrder
	details: ReturnType<typeof useCompleteDetails>
	canEdit: boolean
}

/** Whether the step lacks a required field, and whether the guard requires it at all. */
const fieldState = ({ missing, required }: ReturnType<typeof useCompleteDetails>) => ({
	isMissing: (field: RequiredWorkOrderField) => missing.includes(field),
	isRequired: (field: RequiredWorkOrderField) => required.includes(field),
})

function DeliverableSection({ workOrder: wo, details, canEdit }: SectionProps) {
	const assetTypes = useItems(AxisCode.ASSET_TYPE_CODE)
	const platforms = useItems(AxisCode.CHANNEL)
	const aspectRatios = useItems(AxisCode.ASPECT_RATIO)
	const { isMissing, isRequired } = fieldState(details)
	const isSynced = isFromWorkfront(wo)
	return (
		<DeliverableDetailsCard
			assetTypes={assetTypes}
			platforms={platforms}
			aspectRatios={aspectRatios}
			value={details.form}
			onChange={canEdit ? (patch) => details.update(patch as PackFormEdits) : undefined}
			locked={{
				assetType: isSynced && wo.assetTypeCode !== null,
				platform: isSynced && (wo.tuple?.[AxisCode.CHANNEL]?.length ?? 0) > 0,
			}}
			required={{
				assetType: isRequired(RequiredWorkOrderField.ASSET_TYPE),
				platform: isRequired(RequiredWorkOrderField.PLATFORM),
			}}
			requiresAction={isMissing(RequiredWorkOrderField.ASSET_TYPE) || isMissing(RequiredWorkOrderField.PLATFORM)}
			instructions={
				<PackFileUploads
					workOrder={wo}
					fileType={FileType.PRODUCTION_INSTRUCTIONS}
					kind={INSTRUCTION_FILES}
					label="Upload production instructions"
					canEdit={canEdit}
				/>
			}
		/>
	)
}

function CampaignSection({ workOrder: wo, details, canEdit }: SectionProps) {
	const { form, update } = details
	const { isMissing, isRequired } = fieldState(details)
	// A campaign Workfront set is the sync's: shown, never changed here.
	const edit = canEdit && !(isFromWorkfront(wo) && wo.campaignCode !== null) ? update : undefined
	return (
		<CampaignCard
			isCampaign={form.isCampaign}
			onToggle={edit && ((isCampaign) => edit({ isCampaign }))}
			requiresAction={isMissing(RequiredWorkOrderField.CAMPAIGN_MATERIALS)}
			isRequired={isRequired(RequiredWorkOrderField.CAMPAIGN_MATERIALS)}
			picker={
				<CampaignPicker
					workOrder={wo}
					value={form.campaignCode}
					onChange={edit && ((campaignCode) => edit({ campaignCode }))}
				/>
			}
			mediaPlan={
				form.campaignCode ? (
					<PackFileUploads
						workOrder={wo}
						fileType={FileType.MEDIA_PLAN}
						campaignCode={form.campaignCode}
						kind={DOCUMENT_FILES}
						label="Upload the media plan"
						canEdit={canEdit}
					/>
				) : (
					<p className="text-atlas-label text-atlas-light-dark">Pick a campaign to attach its media plan.</p>
				)
			}
		/>
	)
}

/** The brief as the order holds it, or nothing when it has neither a briefing nor Workfront's sections. */
function descriptionOf(wo: WorkOrder) {
	const sections = isFromWorkfront(wo) ? (wo.externalDescription ?? []) : []
	if (!wo.briefing && sections.length === 0) return undefined
	return (
		<>
			{wo.briefing ? <SafeHtml html={wo.briefing} className="text-atlas-body text-atlas-dark" /> : null}
			<ExternalDescription sections={sections.length ? sections : null} />
		</>
	)
}

/**
 * Task Details (Workfront), the kit's card: the request read-only, then Reference material.
 * Refresh from Workfront gates itself on whether the reader may refresh (the PM or an admin).
 */
function TaskDetailsSection({
	workOrder: wo,
	details,
	canEdit,
	fields,
}: SectionProps & { fields: TaskDetailsField[] }) {
	const [isExpanded, setIsExpanded] = useState(true)
	const [isReferenceExpanded, setIsReferenceExpanded] = useState(true)
	const [packageCode] = sourceMaterialCodes(wo)
	const edit = canEdit ? details.update : undefined
	return (
		<section aria-label="Task Details (Workfront)" className="flex flex-col gap-2">
			{isFromWorkfront(wo) ? (
				<div className="flex justify-end">
					<WorkOrderRefreshFromWorkfront workOrderId={wo.workOrderId} />
				</div>
			) : null}
			<TaskDetailsCard
				title="Task Details (Workfront)"
				isExpanded={isExpanded}
				onExpandedChange={setIsExpanded}
				description={descriptionOf(wo)}
				fields={fields}
				isReferenceExpanded={isReferenceExpanded}
				onReferenceExpandedChange={setIsReferenceExpanded}
				referenceMaterial={
					<ReferenceMaterial
						packageCode={packageCode ? { code: packageCode.code, href: packageCode.url } : null}
						sharepointUrl={details.form.sharepointFolderUrl}
						onSharepointUrlChange={edit && ((sharepointFolderUrl) => edit({ sharepointFolderUrl }))}
						sharepointError={details.sharepointError}
						onCopy={copyToClipboard}
					/>
				}
			/>
		</section>
	)
}

/**
 * Sources verified for the order's PM: everything the designer needs before they start, in the
 * Figma's order. Read-only for an administrator who is not the PM.
 */
export function CompleteDetailsStep({
	workOrder: wo,
	details,
	brand,
	product,
	market,
	canEdit,
}: SectionProps & { brand: string | null; product: string | null; market: StageMarket | null }) {
	const { form } = details
	const taskDetails = useTaskDetails(wo, brand, product, market)
	const legalTexts = useLegalTexts(wo)
	const { isMissing, isRequired } = fieldState(details)
	const edit = canEdit ? details.update : undefined

	return (
		<>
			<TaskDetailsSection workOrder={wo} details={details} canEdit={canEdit} fields={taskDetails} />
			<DeliverableSection workOrder={wo} details={details} canEdit={canEdit} />
			<CopyCard
				mainCopy={form.mainCopy}
				onChange={edit && ((mainCopy) => edit({ mainCopy }))}
				onCopy={copyToClipboard}
				isRequired={isRequired(RequiredWorkOrderField.MAIN_COPY)}
				requiresAction={isMissing(RequiredWorkOrderField.MAIN_COPY)}
				files={
					<PackFileUploads
						workOrder={wo}
						fileType={FileType.COPY_DOCUMENT}
						kind={DOCUMENT_FILES}
						label="Upload copy files"
						canEdit={canEdit}
					/>
				}
			/>
			<LegalContentCard
				isOn={isLegalContentOn(form)}
				onToggle={edit && ((legalContentApplies) => edit({ legalContentApplies }))}
				rows={legalRowsOf(form, legalTexts)}
				onToggleRow={edit && ((flag, isApplicable) => edit({ flags: { [flag]: isApplicable } }))}
			/>
			<CampaignSection workOrder={wo} details={details} canEdit={canEdit} />
		</>
	)
}
